import { useEffect, useState, type ReactNode, type Ref } from 'react';
import { cx } from '../cx';
import { Field, fieldControlClass } from '../Field/Field';

export type RangeSliderProps = {
  label: ReactNode;
  value: number;
  onValueChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  /** Formats a value for display in the field and the min/max captions (use ui/format.ts). */
  format: (value: number) => string;
  /** Accessible text for the slider's current value (defaults to `format`). */
  valueText?: (value: number) => string;
  help?: ReactNode;
  id?: string;
  className?: string;
  /** Goes to the number field, the source of truth. */
  ref?: Ref<HTMLInputElement>;
};

const clampStep = (n: number, min: number, max: number, step: number) => {
  const snapped = Math.round((n - min) / step) * step + min;
  return Math.min(max, Math.max(min, snapped));
};

/**
 * A number field paired with a slider, for calculators and pay filters. The field is the source of
 * truth (type an exact value; it shows formatted when not being edited); the slider is a keyboard-
 * operable shortcut. The visible track and thumb are drawn over a native range input.
 */
export function RangeSlider({
  label,
  value,
  onValueChange,
  min,
  max,
  step = 1,
  format,
  valueText,
  help,
  id,
  className,
  ref,
}: RangeSliderProps) {
  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => setDraft(null), [value]);
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;

  const commit = () => {
    if (draft === null) return;
    const digits = draft.replace(/[^\d-]/g, '');
    const parsed = Number.parseInt(digits, 10);
    if (Number.isFinite(parsed)) onValueChange(clampStep(parsed, min, max, step));
    setDraft(null);
  };

  return (
    <Field label={label} help={help} id={id} className={className}>
      {({ id: fieldId, describedBy, labelId }) => (
        <div className="flex flex-col gap-3">
          <input
            ref={ref}
            id={fieldId}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            aria-describedby={describedBy}
            value={draft ?? format(value)}
            onFocus={() => setDraft(String(value))}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
            }}
            className={cx(fieldControlClass(false), 'tabular-nums')}
          />
          <div className="relative flex h-7 items-center">
            <span className="absolute inset-x-0 h-1.5 rounded-jc-control bg-jc-elevated ring-1 ring-inset ring-jc-border-control" />
            <span className="absolute left-0 h-1.5 rounded-jc-control bg-jc-selected" style={{ width: `${pct}%` }} />
            <input
              type="range"
              min={min}
              max={max}
              step={step}
              value={value}
              aria-labelledby={labelId}
              aria-valuetext={(valueText ?? format)(value)}
              onChange={(e) => onValueChange(Number(e.target.value))}
              className="peer absolute inset-0 m-0 w-full cursor-pointer opacity-0"
            />
            <span
              aria-hidden
              className="pointer-events-none absolute size-6.5 -translate-x-1/2 rounded-full bg-jc-surface shadow-jc-sm ring-2 ring-jc-selected peer-focus-visible:ring-4 peer-focus-visible:ring-jc-focus"
              style={{ left: `${pct}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-jc-ink-secondary tabular-nums">
            <span>{format(min)}</span>
            <span>{format(max)}</span>
          </div>
        </div>
      )}
    </Field>
  );
}
